import { useState } from 'react'
import { toast } from 'sonner'
import { AlertTriangle, Check, CheckCircle2, Copy, Eye, EyeOff } from 'lucide-react'
import { Trans, useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { i18n } from '@/lib/i18n'
import { buildInviteMessage, channelSummary, type NewMemberInfo } from './add-member-helpers'

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch {
    toast.error(i18n.t('team:memberCreatedDialog.clipboardBlocked'))
    return false
  }
}

function CopyRow({ label, value, secret, display }: { label: string; value: string; secret?: boolean; display?: string }) {
  const { t } = useTranslation('team')
  const [copied, setCopied] = useState(false)
  const [shown, setShown] = useState(!secret)
  return (
    <div className="flex items-center gap-2 rounded-md border bg-muted/30 px-3 py-2">
      <div className="min-w-0 flex-1">
        <div className="text-[11px] text-muted-foreground">{label}</div>
        <div className={`truncate text-sm ${secret ? 'font-mono' : ''}`} title={shown ? value : undefined}>
          {shown ? display ?? value : '•'.repeat(Math.min(value.length, 14))}
        </div>
      </div>
      {secret && (
        <button
          type="button"
          onClick={() => setShown((v) => !v)}
          className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
          aria-label={shown ? t('memberCreatedDialog.hidePassword') : t('memberCreatedDialog.showPassword')}
          title={shown ? t('memberCreatedDialog.hidePassword') : t('memberCreatedDialog.showPassword')}
        >
          {shown ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      )}
      <button
        type="button"
        onClick={async () => {
          if (await copyText(value)) {
            setCopied(true)
            setTimeout(() => setCopied(false), 1500)
          }
        }}
        className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
        aria-label={t('memberCreatedDialog.copyField', { field: label.toLowerCase() })}
        title={t('memberCreatedDialog.copyField', { field: label.toLowerCase() })}
      >
        {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
      </button>
    </div>
  )
}

/** Tóm tắt sau khi thêm nhân viên: thông tin đăng nhập + nút chép từng mục / chép cả tin nhắn gửi nhân viên. */
export function MemberCreatedDialog({ info, onClose }: { info: NewMemberInfo | null; onClose: () => void }) {
  const { t } = useTranslation('team')
  const [copiedAll, setCopiedAll] = useState(false)
  if (!info) return null
  return (
    <Dialog
      open
      onOpenChange={(o) => {
        if (!o) {
          setCopiedAll(false)
          onClose()
        }
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-emerald-600" /> {t('memberCreatedDialog.title')}
          </DialogTitle>
          <DialogDescription>{t('memberCreatedDialog.description')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <CopyRow label={t('memberCreatedDialog.fullName')} value={info.fullName} />
          <CopyRow label="Email" value={info.email} />
          <CopyRow label={t('memberCreatedDialog.role')} value={info.roleName} />
          {info.teamName && <CopyRow label={t('memberCreatedDialog.department')} value={info.teamName} />}
          {info.phone && <CopyRow label={t('memberCreatedDialog.phone')} value={info.phone} />}
          {info.channels && info.channels.length > 0 && (
            <CopyRow label={t('memberCreatedDialog.channels')} value={channelSummary(info.channels)} display={channelSummary(info.channels, 3)} />
          )}
          <CopyRow label={t('memberCreatedDialog.password')} value={info.password} secret />
          <CopyRow label={t('memberCreatedDialog.loginLink')} value={info.loginUrl} />
        </div>

        <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            <Trans t={t} i18nKey="memberCreatedDialog.passwordWarning" components={{ b: <b /> }} />
          </span>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={async () => {
              if (await copyText(buildInviteMessage(info))) {
                setCopiedAll(true)
                toast.success(t('memberCreatedDialog.copiedMessage'))
              }
            }}
          >
            {copiedAll ? <Check /> : <Copy />} {t('memberCreatedDialog.copyAll')}
          </Button>
          <Button
            type="button"
            onClick={() => {
              setCopiedAll(false)
              onClose()
            }}
          >
            {t('common:actions.done')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
